/**
 * `location` — `LocationValue` (`specs/M2-tier2-column-types.md` §5.5, `[09§A.7 location]`).
 *
 * `value` is a Google Places payload with **camelCase** keys and `lat`/`lng` as **strings**
 * `[09§A.7 location]`. Four shapes occur across the 137 populated cells of the 2026-09-10
 * snapshot:
 *
 * | keys | cells |
 * |---|---|
 * | `address` `lat` `lng` `city` `street` `streetNumber` `country` `placeId` `changed_at` | 88 |
 * | `address` `lat` `lng` | 28 |
 * | `address` `lat` `lng` `placeId` `changed_at` | 19 |
 * | `address` (+ `changed_at`, `lat`, `lng`) | 2 |
 *
 * `city` / `street` / `country` are **objects** (`{"long_name":"Italia","short_name":"IT"}`) and
 * are frequently `null` even when `address` is set. `text` is `address` verbatim.
 *
 * **No geocoding provider is wired** (the brief: none for now). The editor takes free text plus
 * an optional lat/lng, exactly what monday stores when it has no Place ID: *"Without a Google
 * Place ID only `lat`, `lng`, `address` populate."* A map preview is an open question (q336).
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
import { locationFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
const placePartSchema = z
    .looseObject({ long_name: z.string().nullable().optional(), short_name: z.string().nullable().optional() })
    .nullable();
export const locationValueSchema = z.looseObject({
    address: z.string(),
    /** Strings, not numbers — monday's own JSON `[09§A.7 location]`. */
    lat: z.string().nullable().optional(),
    lng: z.string().nullable().optional(),
    placeId: z.string().nullable().optional(),
    city: placePartSchema.optional(),
    street: placePartSchema.optional(),
    streetNumber: placePartSchema.optional(),
    country: placePartSchema.optional(),
    changed_at: z.string().nullable().optional(),
});
export const locationSettingsSchema = z.looseObject({ hide_footer: z.boolean().optional() });
/** Lat −90..90 exclusive, lng −180..180 inclusive `[09§A.7 location]`. */
function checkCoordinate(raw, field) {
    if (raw === null || raw === undefined || raw === '')
        return null;
    const numeric = typeof raw === 'number' ? raw : Number(String(raw));
    if (!Number.isFinite(numeric))
        throw new ColumnValueError('location', `"${field}" is not a number`);
    const limit = field === 'lat' ? 90 : 180;
    const ok = field === 'lat' ? numeric > -limit && numeric < limit : numeric >= -limit && numeric <= limit;
    if (!ok)
        throw new ColumnValueError('location', `"${field}" ${numeric} is out of range`);
    return String(raw);
}
/** `change_simple_column_value`: `"29.9772962 31.1324955 Giza Pyramid Complex"` `[09§A.7]`. */
function fromSimple(raw) {
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    const match = /^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s+(.+)$/.exec(trimmed);
    if (match === null)
        return { address: trimmed };
    return {
        lat: checkCoordinate(match[1], 'lat'),
        lng: checkCoordinate(match[2], 'lng'),
        address: (match[3] ?? '').trim(),
    };
}
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string')
        return fromSimple(raw);
    if (typeof raw !== 'object') {
        throw new ColumnValueError('location', `expected an object or string, received ${typeof raw}`);
    }
    const record = raw;
    const address = record.address;
    if (address === null || address === undefined || address === '')
        return null;
    if (typeof address !== 'string')
        throw new ColumnValueError('location', '"address" must be a string');
    const value = { address };
    const lat = checkCoordinate(record.lat, 'lat');
    const lng = checkCoordinate(record.lng, 'lng');
    if (lat !== null)
        value.lat = lat;
    if (lng !== null)
        value.lng = lng;
    const placeId = record.placeId ?? record.place_id;
    if (typeof placeId === 'string' && placeId !== '')
        value.placeId = placeId;
    if (keepVolatile) {
        // The Places sub-objects round-trip untouched; we never re-derive one from the address.
        for (const key of ['city', 'street', 'streetNumber', 'country']) {
            if (key in record)
                value[key] = record[key];
        }
        if (typeof record.changed_at === 'string')
            value.changed_at = record.changed_at;
    }
    return value;
}
function partOf(value, key, short) {
    const part = value[key];
    if (part === null || part === undefined || typeof part !== 'object')
        return null;
    const record = part;
    const picked = short ? record.short_name : record.long_name;
    return typeof picked === 'string' && picked !== '' ? picked : null;
}
export const locationColumn = defineColumnType({
    type: 'location',
    title: 'Location',
    description: 'An address with optional coordinates.',
    apiFragmentName: 'LocationValue',
    legacyTypeName: 'location',
    idPrefix: 'location',
    valueSchema: locationValueSchema,
    settingsSchema: locationSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: locationSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    /** *"Clearing by passing an empty string is not supported"* — `null` only `[09§A.7]`. */
    clearForms: ['null'],
    filterConditions: locationFilterConditions,
    filterOperators: operatorsOf(locationFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return normalize(raw, false);
    },
    parseSimpleValue(raw) {
        return raw === null ? null : fromSimple(raw);
    },
    toText(value) {
        return value?.address ?? '';
    },
    toSortKeys(value) {
        return { text: textShadow(value?.address ?? null), number: null, date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return (a.address === b.address &&
            (a.lat ?? null) === (b.lat ?? null) &&
            (a.lng ?? null) === (b.lng ?? null) &&
            (a.placeId ?? null) === (b.placeId ?? null));
    },
    toActivityValue(value) {
        return value === null ? null : { address: value.address, lat: value.lat ?? null, lng: value.lng ?? null };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        return value === null ? null : { ...value };
    },
    toSimpleValue(value) {
        if (value === null)
            return '';
        return value.lat && value.lng ? `${value.lat} ${value.lng} ${value.address}` : value.address;
    },
    fromMondaySettings(raw) {
        return locationSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    /** The fragment flattens the Places objects and turns lat/lng into **numbers** `[cvs§2]`. */
    toApiFragment(value) {
        if (value === null)
            return {};
        return {
            address: value.address,
            city: partOf(value, 'city', false),
            city_short: partOf(value, 'city', true),
            country: partOf(value, 'country', false),
            country_short: partOf(value, 'country', true),
            lat: value.lat === undefined || value.lat === null ? null : Number(value.lat),
            lng: value.lng === undefined || value.lng === null ? null : Number(value.lng),
            place_id: value.placeId ?? null,
            street: partOf(value, 'street', false),
            street_short: partOf(value, 'street', true),
            street_number: partOf(value, 'streetNumber', false),
            street_number_short: partOf(value, 'streetNumber', true),
            updated_at: toFragmentTimestamp(value.changed_at),
        };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value) {
        return value === null ? [] : [{ key: value.address, label: value.address }];
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map